import React from "react";
import { Mic, Pencil, CheckCircle2, Bot, User, Play, Sparkles } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal/ScrollReveal";
import Accordion from "@/components/Accordion/Accordion";

const STEPS = [
  {
    eyebrow: "Etapa 01",
    title: "Iniciar sessão",
    body: "Profissional abre o app, seleciona o paciente, inicia a gravação. Sem fricção, sem configurações por sessão.",
    icon: <Play size={20} />,
  },
  {
    eyebrow: "Etapa 02",
    title: "Gravar",
    body: "Fala naturalmente durante ou após a sessão. Transcrição em tempo real via Groq Whisper Large v3, otimizada para vocabulário clínico em PT-BR.",
    icon: <Mic size={20} />,
  },
  {
    eyebrow: "Etapa 03",
    title: "Geração",
    body: "Gemini 2.5 Flash estrutura o prontuário nos campos COFFITO. O processo leva ~15 segundos do fim da gravação ao rascunho pronto para revisão.",
    icon: <Sparkles size={20} />,
  },
  {
    eyebrow: "Etapa 04",
    title: "Revisão",
    body: "Profissional lê, edita campos inline, complementa se necessário. Nenhum campo \"Não informado\" bloqueia a aprovação — você decide o que é suficiente.",
    icon: <Pencil size={20} />,
  },
  {
    eyebrow: "Etapa 05",
    title: "Aprovação",
    body: "Um clique em Aprovar finaliza o prontuário, com registro de quem aprovou, data e hora, pronto para exportação em PDF.",
    icon: <CheckCircle2 size={20} />,
  },
];

const FAQ = [
  {
    q: "A IA pode errar no prontuário?",
    a: "Sim. Por isso a revisão humana é obrigatória e arquitetural — nenhum documento é salvo sem aprovação explícita. O sistema foi projetado para apoiar seu julgamento, não substituí-lo.",
  },
  {
    q: "Meus dados de pacientes ficam seguros?",
    a: "Dados armazenados no Brasil, tratados conforme LGPD Art. 11 (dados sensíveis de saúde). Isolamento por usuário via JWT. Logs de auditoria em cada operação.",
  },
  {
    q: "O prontuário gerado é aceito pelo COFFITO?",
    a: "O Akali AI estrutura o prontuário conforme os campos da Resolução COFFITO 414/2012. A conformidade final depende da revisão e aprovação do profissional habilitado.",
  },
  {
    q: "Funciona offline?",
    a: "Não. Transcrição e geração requerem conexão com internet. O app funciona em qualquer navegador moderno, desktop ou mobile.",
  },
  {
    q: "Posso cancelar a qualquer momento?",
    a: "Sim. Sem fidelidade, sem multa. Seus dados ficam acessíveis por 30 dias após o cancelamento para export.",
  },
  {
    q: "Como funciona para clínicas com múltiplos profissionais?",
    a: "O plano Clínica oferece dashboard centralizado, gestão de assentos e relatórios de produtividade. Cada profissional tem acesso isolado aos seus próprios prontuários.",
  },
];

const ROLES = [
  ["Transcrição de áudio → PT-BR", "Inicia e encerra a gravação"],
  ["Estruturação nos campos COFFITO", "Revisa e corrige se necessário"],
  ["Preenche hipótese diagnóstica", "Valida clinicamente o conteúdo"],
  ["Gera plano terapêutico sugerido", "Aprova ou rejeita o prontuário"],
  ["Formata para exportação em PDF", "Aprova, gerando registro de aprovação com trilha de auditoria"],
];

export default function HowItWorksPage() {
  return (
    <>
      <section className="page-intro" data-testid="page-intro">
        <div className="container">
          <ScrollReveal>
            <span className="eyebrow">COMO FUNCIONA</span>
            <h1 className="h-1">Como o Akali AI funciona</h1>
            <p className="body-lg page-intro-sub">
              Do áudio ao prontuário aprovado em minutos. Cinco etapas, uma
              única responsabilidade clínica: a sua.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="section section-surface" data-testid="section-problem-detail">
        <div className="container">
          <ScrollReveal>
            <div className="section-head">
              <span className="eyebrow">O CONTEXTO</span>
              <h2 className="h-2">O problema que resolvemos</h2>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <div className="prose">
              <p>
                O fisioterapeuta brasileiro perde em média <strong>45 minutos
                por dia</strong> com documentação manual: anotações em papel
                que precisam ser passadas a limpo, prontuários no WhatsApp que
                não atendem ao COFFITO, planilhas que não conversam entre si.
              </p>
              <p>
                Em uma clínica de 5 profissionais com 10 atendimentos por
                profissional/dia, isso representa mais de <strong>80 horas
                mensais</strong> de tempo clínico perdido em digitação. Tempo
                que poderia ser convertido em pacientes atendidos, em estudos
                de caso, ou simplesmente em descanso.
              </p>
              <p>
                O risco regulatório agrava o cenário. A Resolução COFFITO
                414/2012 exige campos específicos no prontuário fisioterápico;
                a fiscalização não é teórica, e auditorias internas em clínicas
                têm encontrado lacunas que comprometem a defesa profissional em
                situações de perícia.
              </p>
              <p>
                O Akali AI nasceu para resolver os dois lados desta equação ao
                mesmo tempo — devolver o tempo do profissional sem abrir mão da
                conformidade regulatória.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="section section-deep" data-testid="section-process">
        <div className="container">
          <ScrollReveal>
            <div className="section-head">
              <span className="eyebrow">O FLUXO COMPLETO</span>
              <h2 className="h-2">Da voz ao prontuário aprovado</h2>
            </div>
          </ScrollReveal>

          <div className="process-steps">
            {STEPS.map((s, i) => (
              <ScrollReveal
                key={s.title}
                delay={0.05 * i}
                className="process-step glass"
                data-testid={`process-step-${i + 1}`}
              >
                <div className="process-step-visual">
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
                    <span style={{ color: "var(--color-accent)" }}>{s.icon}</span>
                    <span>{s.eyebrow}</span>
                  </div>
                </div>
                <div className="process-step-content">
                  <span className="process-step-eyebrow">{s.eyebrow}</span>
                  <h3 className="process-step-title">{s.title}</h3>
                  <p className="process-step-body">{s.body}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-surface" data-testid="section-roles">
        <div className="container">
          <ScrollReveal>
            <div className="section-head">
              <span className="eyebrow">DIVISÃO DE RESPONSABILIDADES</span>
              <h2 className="h-2">O que a IA faz. O que você faz.</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="roles-table" data-testid="roles-table">
              <div className="roles-row roles-header">
                <div className="roles-cell">
                  <Bot size={18} color="var(--color-accent)" /> Akali AI faz
                </div>
                <div className="roles-cell">
                  <User size={18} color="var(--color-accent)" /> Você faz
                </div>
              </div>
              {ROLES.map((row) => (
                <div key={row[0]} className="roles-row">
                  <div className="roles-cell">{row[0]}</div>
                  <div className="roles-cell">{row[1]}</div>
                </div>
              ))}
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <p className="roles-note">
              O prontuário final é 100% de responsabilidade do profissional.
              A IA é copiloto, não piloto.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="section section-deep" data-testid="section-faq">
        <div className="container">
          <ScrollReveal>
            <div className="section-head">
              <span className="eyebrow">PERGUNTAS FREQUENTES</span>
              <h2 className="h-2">Antes que você pergunte</h2>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <Accordion items={FAQ} />
          </ScrollReveal>
        </div>
      </section>
    </>
  );
}
